<script setup lang="ts">
import { computed } from 'vue';
import SavedViewRowMenu from '@/layouts/frame/SavedViewRowMenu.vue';
import { useSavedViews } from '@/composables/useSavedViews';
import type { SavedViewRecord } from '@/utils/savedViews';

/**
 * The end of the open view's sidebar row, on a list: a dot and a Save button
 * while the list has unsaved changes to the view, then the view's "…" menu,
 * which also offers "Save changes". There is no Save button in the toolbar.
 *
 * Its own component so the saved-views lookup it needs runs only for the open
 * view on a list, not on every page the sidebar sits beside.
 */
const props = defineProps<{ view: SavedViewRecord }>();

const { activeView, isModified, handlers } = useSavedViews();
const hasUnsavedChanges = computed(() => activeView.value?.id === props.view.id && isModified.value);

function saveChanges() {
    handlers.onUpdateActive();
}
</script>

<template>
  <span
    v-if="hasUnsavedChanges"
    class="mr-1.5 size-1.5 shrink-0 rounded-full bg-accent-border"
    title="Unsaved changes"
    data-testid="section-view-modified"
  />
  <button
    v-if="hasUnsavedChanges"
    type="button"
    title="Save the changes to this view"
    data-testid="section-view-save"
    class="mr-0.5 h-6 shrink-0 rounded-md px-1.5 text-xs font-medium text-foreground hover:bg-background"
    @click="saveChanges"
  >
    Save
  </button>
  <SavedViewRowMenu :view="view" :can-save-changes="hasUnsavedChanges" @save-changes="saveChanges" />
</template>
